import React, { useEffect, useState } from 'react';
import {
  AreaChart, Area, LineChart, Line, PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  StarIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { adminAPI } from '../services/api';
import KPICard from '../components/KPICard';
import Avatar from '../components/Avatar';
import styles from './DashboardPage.module.css';
import { formatDistanceToNow, format } from 'date-fns';
import badgeUsers from '../assets/icons/badge-users.png';
import badgeStar from '../assets/icons/badge-star.png';
import badgeDollar from '../assets/icons/badge-dollar.png';
import badgeClock from '../assets/icons/badge-clock.png';
import badgeCheck from '../assets/icons/badge-check.png';
import actSignup from '../assets/icons/act-signup.png';
import actVip from '../assets/icons/act-vip.png';
import actRenewal from '../assets/icons/act-renewal.png';
import actCoupon from '../assets/icons/act-coupon.png';
import actReferral from '../assets/icons/act-referral.png';

// Brand palette — thetoystorelb.com
const BLUE='#2B9DD4', BLUE_DARK='#0F559D', GREEN='#10B981', GOLD='#F5C842', GOLD_DARK='#D9A417', RED='#E11732';
const STATUS_COLORS = { active:GREEN, expired:RED, pending:GOLD, inactive:'#93A6B5' };
const ACTIVITY_META = {
  new_user:    { img:actSignup,   color:BLUE },
  vip_purchase:{ img:actVip,      color:GOLD },
  renewal:     { img:actRenewal,  color:GREEN },
  coupon_used: { img:actCoupon,   color:RED },
  referral:    { img:actReferral, color:BLUE_DARK },
};

const fmt    = n => (n??0).toLocaleString();
const fmtUSD = n => `$${(n??0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const mo     = iso => { try { return format(new Date(iso),'MMM'); } catch { return ''; } };

const Tip = ({active,payload,label}) => {
  if (!active||!payload?.length) return null;
  return (
    <div style={{background:'#fff',border:'1px solid #E2E8F0',borderRadius:10,padding:'10px 14px',boxShadow:'0 4px 16px rgba(0,0,0,.1)',fontSize:12}}>
      <p style={{fontWeight:700,marginBottom:4}}>{label}</p>
      {payload.map(p=><p key={p.dataKey} style={{color:p.color}}>{p.name}: <strong>{p.dataKey==='revenue'?fmtUSD(p.value):p.value}</strong></p>)}
    </div>
  );
};

function Skel({h=120}) { return <div className="skeleton" style={{height:h,borderRadius:12}} />; }

export default function DashboardPage() {
  const [overview, setOverview] = useState(null);
  // The real customer base from Business Central, alongside the app figures.
  const [bc, setBc] = useState(null);
  const [monthly,  setMonthly]  = useState([]);
  const [growth,   setGrowth]   = useState([]);
  const [activity, setActivity] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    Promise.all([
      adminAPI.getDashboard(),
      adminAPI.getDashboardGrowth(),
      adminAPI.getDashboardActivity(),
      adminAPI.getDashboardExpiring(),
    ]).then(([dash, gr, act, exp]) => {
      setOverview(dash.overview);
      setBc(dash.bc || null);
      setMonthly(dash.renewalsByMonth||[]);
      setGrowth(gr.growth||[]);
      setActivity(act.activity||[]);
      setExpiring(exp.expiring||[]);
    }).catch(console.error).finally(()=>setLoading(false));
  }, []);

  const donutData = overview
    ? [{name:'Active',value:overview.activeMembers||0},{name:'Expired',value:overview.expiredMembers||0},{name:'Pending',value:overview.pendingMembers||0}].filter(d=>d.value>0)
    : [];
  const totalMem = donutData.reduce((s,d)=>s+d.value,0);

  return (
    <div>
      <div className="page-header">
        <div><h1>Dashboard</h1><p>Real-time overview of your VIP membership program.</p></div>
      </div>

      {/* KPI row */}
      <div className={styles.kpiGrid}>
        {loading ? [0,1,2,3].map(i=><Skel key={i}/>) : (<>
          <KPICard label="Total App Users"     value={fmt(overview?.totalUsers)}     badge={badgeUsers}  color={BLUE}   sub={`${fmt(overview?.activeMembers)} VIP members`}/>
          <KPICard label="Active VIP Members"  value={fmt(overview?.activeMembers)}  badge={badgeStar}   color={GOLD}   sub={`${overview?.totalUsers?Math.round(overview.activeMembers/overview.totalUsers*100):0}% conversion`}/>
          <KPICard label="Total Revenue"       value={fmtUSD(overview?.totalRevenue)}badge={badgeDollar} color={GREEN}  sub={`${fmt(overview?.couponRedemptions)} coupons redeemed`}/>
          <KPICard label="Expiring in 30 Days" value={fmt(overview?.expiringIn30)}   badge={overview?.expiringIn30>0?badgeClock:badgeCheck} color={overview?.expiringIn30>0?RED:GREEN} sub={`${fmt(overview?.expiringIn60)} in 60 days`}/>
        </>)}
      </div>

      {/* Business Central — the real customer base */}
      {bc && (
        <>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>Business Central</h2>
            <span className={styles.sectionNote}>
              {Number(bc.total).toLocaleString()} customers
              {bc.last_synced_at ? ' · synced ' + new Date(bc.last_synced_at).toLocaleString('en-GB') : ''}
            </span>
          </div>
          <div className={styles.kpiGrid}>
            <KPICard label="VIP Members"        value={fmt(bc.vip)}             badge={badgeStar}  color={GOLD}  sub={`${fmt(bc.silver)} Silver`}/>
            <KPICard label="Have the App"       value={fmt(bc.with_app)}        badge={badgeUsers} color={BLUE}  sub={`of ${fmt(bc.vip)} VIPs`}/>
            <KPICard label="VIP, No App"        value={fmt(bc.vip_without_app)} badge={badgeUsers} color={BLUE}  sub="reachable by WhatsApp"/>
            <KPICard label="VIP Expiring 30d"   value={fmt(bc.vip_expiring_30)} badge={bc.vip_expiring_30>0?badgeClock:badgeCheck} color={bc.vip_expiring_30>0?RED:GREEN} sub={`${fmt(bc.vip_expiring_60)} in 60 days`}/>
          </div>
        </>
      )}

      {/* Revenue + Status donut */}
      <div className={styles.chartsRow1}>
        <div className={`card ${styles.chartCard}`}>
          <div className="card-title"><h2>Revenue Over Time</h2><span style={{fontSize:12,color:'var(--text-muted)'}}>Last 6 months</span></div>
          {loading?<Skel h={220}/>:(
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={monthly.map(m=>({...m,month:mo(m.month)}))}>
                <defs><linearGradient id="rg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={BLUE} stopOpacity={.18}/><stop offset="95%" stopColor={BLUE} stopOpacity={0}/></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                <XAxis dataKey="month" tick={{fontSize:11,fill:'#94A3B8'}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontSize:11,fill:'#94A3B8'}} axisLine={false} tickLine={false} tickFormatter={v=>`$${v}`}/>
                <Tooltip content={<Tip/>}/>
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke={BLUE} strokeWidth={2.5} fill="url(#rg)" dot={false}/>
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={`card ${styles.chartCard}`}>
          <div className="card-title"><h2>Membership Status</h2></div>
          {loading?<Skel h={280}/>:(<>
            <div style={{position:'relative'}}>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={donutData} cx="50%" cy="50%" innerRadius={60} outerRadius={84} paddingAngle={3} dataKey="value">
                    {donutData.map(e=><Cell key={e.name} fill={STATUS_COLORS[e.name.toLowerCase()]}/>)}
                  </Pie>
                  <Tooltip formatter={v=>[fmt(v),'']}/>
                </PieChart>
              </ResponsiveContainer>
              <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',textAlign:'center',pointerEvents:'none'}}>
                <div style={{fontSize:26,fontWeight:800,color:'var(--text)'}}>{fmt(totalMem)}</div>
                <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'.5px'}}>Total</div>
              </div>
            </div>
            <div className={styles.donutLegend}>
              {donutData.map(d=>(
                <div key={d.name} className={styles.donutLegendItem}>
                  <span style={{width:8,height:8,borderRadius:'50%',background:STATUS_COLORS[d.name.toLowerCase()],flexShrink:0}}/>
                  <span style={{color:'var(--text-muted)',fontSize:12}}>{d.name}</span>
                  <span style={{fontWeight:700,fontSize:12,marginLeft:'auto'}}>{fmt(d.value)}</span>
                </div>
              ))}
            </div>
          </>)}
        </div>
      </div>

      {/* Growth + Renewals bar */}
      <div className={styles.chartsRow2}>
        <div className={`card ${styles.chartCard}`}>
          <div className="card-title"><h2>Member Growth</h2><span style={{fontSize:12,color:'var(--text-muted)'}}>Last 12 months</span></div>
          {loading?<Skel h={200}/>:(
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={growth.map(g=>({...g,month:mo(g.month)}))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                <XAxis dataKey="month" tick={{fontSize:11,fill:'#94A3B8'}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontSize:11,fill:'#94A3B8'}} axisLine={false} tickLine={false} allowDecimals={false}/>
                <Tooltip content={<Tip/>}/>
                <Legend wrapperStyle={{fontSize:12}}/>
                <Line type="monotone" dataKey="new_users"   name="App Users"   stroke={BLUE}     strokeWidth={2.5} dot={{r:3}} activeDot={{r:5}}/>
                <Line type="monotone" dataKey="new_members" name="VIP Members" stroke={GOLD_DARK} strokeWidth={2.5} dot={{r:3}} activeDot={{r:5}}/>
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={`card ${styles.chartCard}`}>
          <div className="card-title"><h2>Monthly Renewals</h2><span style={{fontSize:12,color:'var(--text-muted)'}}>Count</span></div>
          {loading?<Skel h={200}/>:(
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthly.map(m=>({...m,month:mo(m.month)}))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                <XAxis dataKey="month" tick={{fontSize:11,fill:'#94A3B8'}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontSize:11,fill:'#94A3B8'}} axisLine={false} tickLine={false} allowDecimals={false}/>
                <Tooltip content={<Tip/>}/>
                <defs><linearGradient id="bg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={BLUE}/><stop offset="100%" stopColor={BLUE_DARK}/></linearGradient></defs>
                <Bar dataKey="count" name="Renewals" fill="url(#bg)" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Activity + Expiring */}
      <div className={styles.bottomRow}>
        <div className={`card ${styles.activityCard}`}>
          <div className="card-title"><h2>Recent Activity</h2></div>
          {loading?[0,1,2,3,4].map(i=>(
            <div key={i} style={{display:'flex',gap:12,marginBottom:14}}>
              <div className="skeleton" style={{width:36,height:36,borderRadius:10,flexShrink:0}}/>
              <div style={{flex:1}}><div className="skeleton" style={{height:13,width:'70%',marginBottom:6}}/><div className="skeleton" style={{height:11,width:'45%'}}/></div>
            </div>
          )):activity.length===0?<div className="empty-state"><p>No recent activity</p></div>:(
            <div className={styles.activityList}>
              {activity.map((ev,i)=>{
                const {img,color}=ACTIVITY_META[ev.type]||ACTIVITY_META.new_user;
                return (
                  <div key={i} className={styles.activityItem}>
                    <div className={styles.activityIconBox} style={{background:`${color}14`,border:`1px solid ${color}22`}}>
                      <img src={img} alt="" className={styles.activityIconImg}/>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div className={styles.activityActor}>{ev.actor}</div>
                      <div className={styles.activityDesc}>{ev.description}</div>
                    </div>
                    <div className={styles.activityTime}>{ev.ts?formatDistanceToNow(new Date(ev.ts),{addSuffix:true}):''}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={`card ${styles.expiringCard}`}>
          <div className="card-title">
            <h2>Expiring Soon</h2>
            {expiring.length>0&&<span className="badge badge-warning" style={{display:'inline-flex',alignItems:'center',gap:4}}><ExclamationTriangleIcon style={{width:11,height:11}}/>{expiring.length} members</span>}
          </div>
          {loading?[0,1,2].map(i=><div key={i} className="skeleton" style={{height:52,borderRadius:10,marginBottom:8}}/>):
            expiring.length===0?(
              <div className="empty-state"><StarIcon style={{width:28,height:28}}/><h3>All good!</h3><p>No members expiring in the next 30 days.</p></div>
            ):(
              <div className={styles.expiringList}>
                {expiring.map((m,i)=>(
                  <div key={i} className={styles.expiringItem}>
                    <Avatar firstName={m.first_name} lastName={m.last_name} size={36}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div className={styles.expiringName}>{m.first_name} {m.last_name}</div>
                      <div className={styles.expiringCode}>{m.member_code} · {m.mobile}</div>
                    </div>
                    <span className={`badge ${m.days_remaining<=7?'badge-danger':'badge-warning'}`}>{m.days_remaining}d</span>
                  </div>
                ))}
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
}
